'use client';

/**
 * AddWishCard — full-bleed canvas card for the buyer-wish create flow.
 *
 * Thin renderer. The BFF owns:
 *   - Category vocab + image limit (`wish_form_meta`)
 *   - Project options (`obeya_projects`)
 *   - Field labels + help text (server-shipped i18n via `wish_form_meta`)
 *   - Validation rules (`catalog_create_wish` returns `field_errors`)
 *   - Side effects (Neo4j Product + WANTS + HAS_MEDIA)
 *
 * The card collects field values, posts to `catalog_create_wish`, and
 * paints whatever the server returned: success → reset + acknowledge;
 * error → display `field_errors` next to each input.
 *
 * Field layout is bespoke V1. A second "add X" form lands → extract a
 * generic schema-driven FormCard primitive (matches the MatrixCard
 * pattern). Until then, no premature abstraction.
 *
 * @see klyde_edge/discovery/catalog/products.py — _validate_wish_params + create_virtual_product
 * @see klyde_edge/ui_config/handlers/catalog.py — wish_form_meta + catalog_create_wish
 *
 * @layer presentation
 * @arch_component_type card
 * @arch_domain canvas / discovery
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { Loader2, Package, Plus, X as XIcon, CheckCircle, AlertCircle } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import { useStrategyData } from '@/lib/api/hooks';
import { api } from '@/lib/api/client';
import { useGraphSelectionStore } from '@/lib/stores/graph-selection-store';
import { useCustomProductEditStore } from '@/lib/stores/custom-product-edit-store';
import { useProductLabel } from '@/lib/stores/total-matrix-store';
import { useCardExport } from '@/lib/design/card-export';
import { useCardIdentity } from '@/lib/design/card-chrome';
import { cn } from '@/lib/utils/cn';

/** CO_+TT_-scoped edit-prefill row from `catalog_custom_product_detail`. */
interface CustomProductDetail {
  semantic_id: string;
  name: string | null;
  category: string | null;
  description: string | null;
  dimensions: string | null;
  materials: string | null;
  quantity: number | null;
  indicative_price_min: number | null;
  indicative_price_max: number | null;
}

interface WishFormMetaOption {
  value: string;
  label: string;
}

interface WishFormMetaPayload {
  categories: WishFormMetaOption[];
  max_images: number;
  labels: Record<string, string>;
  help_text: Record<string, string>;
}

interface ProjectOption {
  semantic_id: string;
  name: string;
}

interface CreateResult {
  success: boolean;
  status?: string;
  semantic_id?: string;
  name?: string;
  project_name?: string | null;
  media_count?: number;
  error?: string | null;
  field_errors?: Record<string, string>;
}

const EMPTY_FORM = {
  name: '',
  project_sid: '',
  project_name: '',
  category: '',
  description: '',
  dimensions: '',
  materials: '',
  indicative_price_min: '',
  indicative_price_max: '',
  quantity: '',
} as const;

export function AddWishCard() {
  // ─── BFF data ───────────────────────────────────────────────────
  const metaQuery = useStrategyData<WishFormMetaPayload>('wish_form_meta', {});
  const projectsQuery = useStrategyData<{ projects?: ProjectOption[]; items?: ProjectOption[] }>(
    'obeya_projects',
    { limit: 50 },
  );

  const meta = metaQuery.data?.data;
  const labels = meta?.labels ?? {};
  const help = meta?.help_text ?? {};
  const maxImages = meta?.max_images ?? 3;

  const projects: ProjectOption[] = useMemo(() => {
    const raw = projectsQuery.data?.data;
    return raw?.projects ?? raw?.items ?? [];
  }, [projectsQuery.data]);

  // Project context from the upstream selection — the explorer
  // trigger ensures these are populated before swapping the canvas
  // to this card, so the form pre-fills without forcing a dropdown.
  const contextProjectSid = useGraphSelectionStore((s) => s.projectId);
  const contextProjectName = useGraphSelectionStore((s) => s.projectContext?.name ?? null);

  // ─── Edit mode ──────────────────────────────────────────────────
  // The Custom Products kebab sets `editSid` + flips the canvas here;
  // this card then prefills from the wish's current fields and submits
  // to catalog_update_custom_product instead of create. `+ Add` clears
  // editSid, so a null editSid is create mode.
  const editSid = useCustomProductEditStore((s) => s.editSid);
  const setEditSid = useCustomProductEditStore((s) => s.setEditSid);
  const queryClient = useQueryClient();
  const isEdit = !!editSid;
  const editTitle = useProductLabel('custom_products_edit_title');
  const editSubtitle = useProductLabel('custom_products_edit_subtitle');
  const saveLabel = useProductLabel('custom_products_save');

  // Prefill source — a CO_+TT_-scoped single-BW_ read (traverse-or-you-don't-
  // see-it): returns the wish's editable fields only if the caller's company
  // WANTS it, within their tenant. The read half of the same boundary the
  // writes enforce — no unscoped browse, no cross-company leak.
  const editSourceQuery = useStrategyData<{ product: CustomProductDetail | null }>(
    'catalog_custom_product_detail', { product_sid: editSid }, isEdit,
  );
  const editRow = editSourceQuery.data?.data?.product ?? null;

  // NOTE: do NOT clear editSid on unmount. Strict Mode (dev) runs
  // mount → cleanup → remount, so an unmount-cleanup wipes the sid the
  // kebab just armed and the form silently falls back to create mode.
  // Clearing is the *entry points'* job and they already do it
  // explicitly: `+` calls setEditSid(null), the kebab's Edit sets the
  // sid. Both arrive before the canvas swaps here.

  // ─── Form state (UI only — no validation rules here) ────────────
  const [form, setForm] = useState<Record<string, string>>({ ...EMPTY_FORM });
  const [images, setImages] = useState<string[]>([]);   // base64 data URLs
  const [submitting, setSubmitting] = useState(false);
  const [serverErrors, setServerErrors] = useState<Record<string, string>>({});
  const [serverMessage, setServerMessage] = useState<string | null>(null);
  const [submittedName, setSubmittedName] = useState<string | null>(null);

  // Scene export — scene-only: a draft form, no payload-bearing query (the
  // project list is chrome). Registers the card's presence + draft state.
  const { contentKey } = useCardIdentity();
  useCardExport(useMemo(() => ({
    content_key: contentKey ?? 'add_wish',
    status: 'loaded' as const,
    query_key: null,
    text: form.name ? `New wish: ${form.name}` : 'Add custom item',
    json: {
      project_sid: form.project_sid || contextProjectSid || null,
      name: form.name || null,
      submitted: submittedName,
    },
  }), [contentKey, form.name, form.project_sid, contextProjectSid, submittedName]));

  // Pre-fill project_sid / project_name from viewport context on
  // mount and whenever context flips. Does not clobber a user-edited
  // form: only fills when the form's project_sid is empty.
  useEffect(() => {
    if (isEdit) return;                 // edit mode owns the form; no project prefill
    if (!contextProjectSid) return;
    setForm((prev) => {
      if (prev.project_sid) return prev;
      return {
        ...prev,
        project_sid: contextProjectSid,
        project_name: contextProjectName ?? '',
      };
    });
  }, [isEdit, contextProjectSid, contextProjectName]);

  // Edit-mode prefill — load the wish's current fields once its browse row
  // arrives. Keyed on editSid so re-arming the kebab on a different wish
  // re-prefills. Project + images are out of scope for edit (the update
  // capability doesn't touch them).
  const prefilledSidRef = useRef<string | null>(null);
  useEffect(() => {
    if (!isEdit) { prefilledSidRef.current = null; return; }
    if (!editRow || prefilledSidRef.current === editSid) return;
    prefilledSidRef.current = editSid;
    setForm({
      name: editRow.name ?? '',
      project_sid: '',
      project_name: '',
      category: editRow.category ?? '',
      description: editRow.description ?? '',
      dimensions: editRow.dimensions ?? '',
      materials: editRow.materials ?? '',
      indicative_price_min: editRow.indicative_price_min != null ? String(editRow.indicative_price_min) : '',
      indicative_price_max: editRow.indicative_price_max != null ? String(editRow.indicative_price_max) : '',
      quantity: editRow.quantity != null ? String(editRow.quantity) : '',
    });
  }, [isEdit, editRow, editSid]);

  const setField = (key: string, value: string) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    if (serverErrors[key]) {
      setServerErrors((prev) => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
    }
  };

  const setProject = (sid: string) => {
    const proj = projects.find((p) => p.semantic_id === sid);
    setField('project_sid', sid);
    setField('project_name', proj?.name ?? '');
  };

  const imageInputRef = useRef<HTMLInputElement | null>(null);
  const onPickImage = async (file: File | undefined | null) => {
    if (!file) return;
    if (images.length >= maxImages) return;
    const b64 = await new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(String(reader.result ?? ''));
      reader.onerror = () => reject(new Error('read failed'));
      reader.readAsDataURL(file);
    });
    // catalog_create_wish expects raw base64 (no data: prefix)
    const stripped = b64.includes(',') ? b64.split(',')[1] : b64;
    setImages((prev) => [...prev, stripped].slice(0, maxImages));
  };

  // ─── Submit ─────────────────────────────────────────────────────
  const submit = async () => {
    if (submitting) return;
    setSubmitting(true);
    setServerErrors({});
    setServerMessage(null);
    setSubmittedName(null);

    // Edit mode → update the BW_ definition (no project / no images).
    if (isEdit) {
      const editPayload = {
        product_sid: editSid,
        name: form.name,
        category: form.category,
        description: form.description,
        dimensions: form.dimensions,
        materials: form.materials,
        indicative_price_min: form.indicative_price_min === '' ? null : Number(form.indicative_price_min),
        indicative_price_max: form.indicative_price_max === '' ? null : Number(form.indicative_price_max),
        quantity: form.quantity === '' ? null : Number(form.quantity),
      };
      const editResult = await api.getStrategyData<CreateResult>(
        'catalog_update_custom_product', editPayload,
      );
      setSubmitting(false);
      if (!editResult.success || !editResult.data?.success) {
        setServerErrors(editResult.data?.field_errors ?? {});
        setServerMessage(editResult.data?.error ?? editResult.error ?? 'Failed to save changes.');
        return;
      }
      setSubmittedName(editResult.data.name ?? form.name);
      setServerMessage(null);
      // Refresh the Custom Products list, then leave edit mode.
      queryClient.invalidateQueries({ queryKey: ['strategy-data'] });
      setEditSid(null);
      return;
    }

    const payload = {
      name: form.name,
      project_sid: form.project_sid,
      project_name: form.project_name,
      category: form.category,
      description: form.description,
      dimensions: form.dimensions,
      materials: form.materials,
      indicative_price_min: form.indicative_price_min === '' ? null : Number(form.indicative_price_min),
      indicative_price_max: form.indicative_price_max === '' ? null : Number(form.indicative_price_max),
      quantity: form.quantity === '' ? null : Number(form.quantity),
      images,
    };

    const result = await api.getStrategyData<CreateResult>('catalog_create_wish', payload);
    setSubmitting(false);

    if (!result.success || !result.data?.success) {
      const fe = result.data?.field_errors ?? {};
      setServerErrors(fe);
      setServerMessage(result.data?.error ?? result.error ?? 'Failed to create wish.');
      return;
    }

    setSubmittedName(result.data.name ?? form.name);
    setServerMessage(null);
    // Refresh the Custom Products list so the new wish appears.
    queryClient.invalidateQueries({ queryKey: ['strategy-data'] });
    setForm({ ...EMPTY_FORM });
    setImages([]);
  };

  // ─── Render ─────────────────────────────────────────────────────
  if (metaQuery.isLoading) {
    return <Shell><Loading /></Shell>;
  }
  if (!meta) {
    return <Shell><Empty msg="Form unavailable" /></Shell>;
  }

  return (
    <Shell
      title={isEdit ? editTitle : labels.card_title}
      subtitle={isEdit ? editSubtitle : labels.card_subtitle}
    >
      <form
        onSubmit={(e) => { e.preventDefault(); void submit(); }}
        className="h-full overflow-auto p-4 space-y-5"
      >
        {/* Identity */}
        <Section title={labels.section_identity}>
          <Field label={labels.field_name} error={serverErrors.name} help={help.name}>
            <input
              type="text"
              value={form.name}
              onChange={(e) => setField('name', e.target.value)}
              className={inputClass(!!serverErrors.name)}
              autoFocus
            />
          </Field>

          {/* Project placement is set at create and not edited here — the
              update capability doesn't touch it. Hidden in edit mode. */}
          {!isEdit && (
            <Field label={labels.field_project} error={serverErrors.project_sid}>
              {projectsQuery.isLoading ? (
                <div className="text-xs text-muted-foreground">Loading projects…</div>
              ) : projects.length === 0 ? (
                <div className="text-xs text-muted-foreground">{labels.no_projects}</div>
              ) : (
                <select
                  value={form.project_sid}
                  onChange={(e) => setProject(e.target.value)}
                  className={inputClass(!!serverErrors.project_sid)}
                >
                  <option value="">{labels.select_project}</option>
                  {projects.map((p) => (
                    <option key={p.semantic_id} value={p.semantic_id}>{p.name}</option>
                  ))}
                </select>
              )}
            </Field>
          )}

          <Field label={labels.field_category} error={serverErrors.category}>
            <select
              value={form.category}
              onChange={(e) => setField('category', e.target.value)}
              className={inputClass(!!serverErrors.category)}
            >
              <option value="">{labels.select_category}</option>
              {meta.categories.map((c) => (
                <option key={c.value} value={c.value}>{c.label}</option>
              ))}
            </select>
          </Field>
        </Section>

        {/* Details */}
        <Section title={labels.section_details}>
          <Field label={labels.field_description} error={serverErrors.description}>
            <textarea
              value={form.description}
              onChange={(e) => setField('description', e.target.value)}
              className={cn(inputClass(!!serverErrors.description), 'min-h-[4rem]')}
              rows={3}
            />
          </Field>

          <div className="grid grid-cols-2 gap-3">
            <Field label={labels.field_dimensions} error={serverErrors.dimensions} help={help.dimensions}>
              <input
                type="text"
                value={form.dimensions}
                onChange={(e) => setField('dimensions', e.target.value)}
                className={inputClass(!!serverErrors.dimensions)}
              />
            </Field>
            <Field label={labels.field_materials} error={serverErrors.materials} help={help.materials}>
              <input
                type="text"
                value={form.materials}
                onChange={(e) => setField('materials', e.target.value)}
                className={inputClass(!!serverErrors.materials)}
              />
            </Field>
          </div>

          <div className="grid grid-cols-3 gap-3">
            <Field label={labels.field_price_min} error={serverErrors.indicative_price_min}>
              <input
                type="number"
                min="0"
                value={form.indicative_price_min}
                onChange={(e) => setField('indicative_price_min', e.target.value)}
                className={inputClass(!!serverErrors.indicative_price_min)}
              />
            </Field>
            <Field label={labels.field_price_max} error={serverErrors.indicative_price_max}>
              <input
                type="number"
                min="0"
                value={form.indicative_price_max}
                onChange={(e) => setField('indicative_price_max', e.target.value)}
                className={inputClass(!!serverErrors.indicative_price_max)}
              />
            </Field>
            <Field label={labels.field_quantity} error={serverErrors.quantity}>
              <input
                type="number"
                min="1"
                value={form.quantity}
                onChange={(e) => setField('quantity', e.target.value)}
                className={inputClass(!!serverErrors.quantity)}
              />
            </Field>
          </div>
        </Section>

        {/* Images — edit mode skips media (the update capability doesn't
            touch HAS_MEDIA; reference images are a create-time concern). */}
        {!isEdit && (
        <Section
          title={labels.section_images}
          help={(help.images ?? '').replace('{max}', String(maxImages))}
        >
          <div className="flex flex-wrap gap-2 items-start">
            {images.map((b64, idx) => (
              <div key={idx} className="relative w-20 h-20 rounded border border-border overflow-hidden">
                {/* eslint-disable-next-line @next/next/no-img-element */}
                <img src={`data:image/jpeg;base64,${b64}`} alt="" className="w-full h-full object-cover" />
                <button
                  type="button"
                  onClick={() => setImages((prev) => prev.filter((_, i) => i !== idx))}
                  className="absolute top-0.5 right-0.5 p-0.5 rounded-full bg-background/80 text-foreground hover:bg-destructive hover:text-destructive-foreground"
                  aria-label="Remove image"
                >
                  <XIcon className="w-3 h-3" />
                </button>
              </div>
            ))}
            {images.length < maxImages && (
              <>
                <input
                  ref={imageInputRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => { void onPickImage(e.target.files?.[0]); e.target.value = ''; }}
                />
                <button
                  type="button"
                  onClick={() => imageInputRef.current?.click()}
                  className="w-20 h-20 rounded border-2 border-dashed border-border flex items-center justify-center text-muted-foreground hover:text-foreground hover:border-primary/50"
                  aria-label="Add image"
                >
                  <Plus className="w-5 h-5" />
                </button>
              </>
            )}
          </div>
          {serverErrors.images && <ErrorLine msg={serverErrors.images} />}
        </Section>
        )}

        {/* Server-level message */}
        {serverMessage && (
          <div className="flex items-start gap-2 text-xs p-2 rounded border border-destructive/40 bg-destructive/5 text-destructive">
            <AlertCircle className="w-3.5 h-3.5 shrink-0 mt-0.5" /> {serverMessage}
          </div>
        )}
        {submittedName && (
          <div className="flex items-start gap-2 text-xs p-2 rounded border border-success/40 bg-success/5 text-success">
            <CheckCircle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
            {(labels.submitted ?? 'Added')}: {submittedName}
          </div>
        )}

        {/* Submit */}
        <div className="flex items-center justify-end">
          <button
            type="submit"
            disabled={submitting}
            className={cn(
              'inline-flex items-center gap-2 px-3 py-1.5 rounded-md text-sm font-medium',
              'bg-primary text-primary-foreground hover:bg-primary/90',
              'disabled:opacity-50 disabled:cursor-not-allowed',
            )}
          >
            {submitting && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
            {submitting
              ? (labels.submitting ?? 'Adding…')
              : (isEdit ? saveLabel : (labels.submit ?? 'Add'))}
          </button>
        </div>
      </form>
    </Shell>
  );
}

// ─── helpers ─────────────────────────────────────────────────────

function inputClass(hasError: boolean): string {
  return cn(
    'w-full px-2.5 py-1.5 rounded-md border bg-background text-sm',
    'placeholder:text-muted-foreground/60 focus:outline-none focus:ring-1',
    hasError
      ? 'border-destructive focus:ring-destructive'
      : 'border-border focus:ring-primary',
  );
}

function Section({
  title,
  help,
  children,
}: {
  title?: string;
  help?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="space-y-2">
      {title && (
        <div className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
          {title}
        </div>
      )}
      {help && <div className="text-[11px] text-muted-foreground">{help}</div>}
      <div className="space-y-3">{children}</div>
    </div>
  );
}

function Field({
  label,
  error,
  help,
  children,
}: {
  label?: string;
  error?: string;
  help?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="space-y-1">
      {label && <label className="text-xs font-medium text-foreground">{label}</label>}
      {children}
      {help && !error && <div className="text-[11px] text-muted-foreground">{help}</div>}
      {error && <ErrorLine msg={error} />}
    </div>
  );
}

function ErrorLine({ msg }: { msg: string }) {
  return (
    <div className="text-[11px] text-destructive flex items-center gap-1">
      <AlertCircle className="w-3 h-3 shrink-0" /> {msg}
    </div>
  );
}

function Shell({
  title,
  subtitle,
  children,
}: {
  title?: string;
  subtitle?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="h-full w-full flex flex-col bg-card rounded-xl border border-border overflow-hidden">
      <div className="flex items-center gap-2 px-4 py-3 border-b border-border shrink-0">
        <Package className="w-3.5 h-3.5 text-violet-400" />
        <div className="min-w-0 flex-1">
          <div className="text-[11px] font-semibold text-foreground uppercase tracking-wider truncate">
            {title ?? 'Add custom item'}
          </div>
          {subtitle && (
            <div className="text-[10px] text-muted-foreground truncate">{subtitle}</div>
          )}
        </div>
      </div>
      <div className="flex-1 min-h-0">{children}</div>
    </div>
  );
}

function Loading() {
  return (
    <div className="h-full flex items-center justify-center gap-2 text-xs text-muted-foreground">
      <Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading…
    </div>
  );
}

function Empty({ msg }: { msg: string }) {
  return (
    <div className="h-full flex flex-col items-center justify-center text-center px-4 text-muted-foreground">
      <Package className="w-8 h-8 mb-2 opacity-40" />
      <p className="text-sm">{msg}</p>
    </div>
  );
}
